import { useStyles } from "./TrendReport.styles";
import { Box } from "@mui/material";
import ReportCard from "components/ReportCard";
import DowntimeSummaryByProductionLine from "./components/DowntimeSummaryByProductionLine";
import { useTranslation } from "react-i18next";
import DowntimeSummaryByProductionLineCheckbox from "./components/DowntimeSummaryByProductionLine/DowntimeSummaryByProductionLineCheckbox/DowntimeSummaryByProductionLineCheckbox.view";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useParams } from "react-router-dom";
import { MillDetailEntity } from "entities/MillDetail/MillDetailEntity";
import { useContext, useEffect, useMemo, useState } from "react";
import { TrendReportContext } from "contexts/TrendReportContext/TrendReportContext";
import DowntimeSummaryByFailureType from "./components/DowntimeSummaryByFailureType";
import DowntimeSummaryByFailureTypeCheckbox from "./components/DowntimeSummaryByFailureType/DowntimeSummaryByFailureTypeCheckbox/DowntimeSummaryByFailureTypeCheckbox.view";
import DowntimeFailureTypeByMachineCenter from "./components/DowntimeFailureTypeByMachineCenter";
import DowntimeFailureTypeByMachineCenterCheckbox from "./components/DowntimeFailureTypeByMachineCenter/DowntimeFailureTypeByMachineCenterCheckbox/DowntimeFailureTypeByMachineCenterCheckbox.view";
import TopFiveFailureCodes from "./components/TopFiveFailureCodes";
import TopFiveFailureCodesCheckbox from "./components/TopFiveFailureCodes/TopFiveFailureCodesCheckbox/TopFiveFailureCodesCheckbox.view";
import TopFiveSecondaryFailureCodes from "./components/TopFiveSecondaryFailureCodes";
import TopFiveSecondaryFailureCodesCheckbox from "./components/TopFiveSecondaryFailureCodes/TopFiveSecondaryFailureCodesCheckbox/TopFiveSecondaryFailureCodesCheckbox.view";
import { WeekTrend } from "./components/WeekTrend/WeekTrend.view";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";

const TrendReport = () => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { millId } = useParams();

    const {
        handleSetMillCountryCode,
        selectedFailureType,
        selectedFailureCode,
        selectedProductionLine,
        selectedMachineCenter,
        selectedSecondaryFailureCode,
    } = useContext(TrendReportContext);

    const { millCountryCode } = MillsEntity.GetMillCountryCode(Number(millId));
    const { countryCode: corporateCountryCode } = MillDetailEntity.GetCorporateZipcodeAndCountry(Number(millId));
    const [countryCode, setCountryCode] = useState<string>("");

    const defaultFailureTypeTranslatedName = translateDefaultEntity(
        i18n.language,
        selectedFailureType,
        "name",
        DefaultTranslationFieldsInTable.FAILURE_TYPE,
    );

    const defaultFailureCodeTranslatedName = translateDefaultEntity(
        i18n.language,
        selectedFailureCode,
        "name",
        DefaultTranslationFieldsInTable.FAILURE_CODE,
    );

    const defaultSecondaryFailureCodeTranslatedName = translateDefaultEntity(
        i18n.language,
        selectedSecondaryFailureCode,
        "name",
        DefaultTranslationFieldsInTable.FAILURE_CODE,
    );

    const weekTrendTitle = useMemo(
        () =>
            [
                selectedProductionLine?.name,
                defaultFailureTypeTranslatedName,
                defaultFailureCodeTranslatedName,
                defaultSecondaryFailureCodeTranslatedName,
                selectedMachineCenter?.name,
            ]
                .filter((value) => {
                    return Boolean(value);
                })
                .join(", "),
        [
            defaultFailureCodeTranslatedName,
            defaultFailureTypeTranslatedName,
            defaultSecondaryFailureCodeTranslatedName,
            selectedMachineCenter?.name,
            selectedProductionLine?.name,
        ],
    );

    const topFiveFailureCodesTitle = useMemo(() => {
        if (selectedProductionLine?.id && selectedFailureType?.id) {
            return t("locations.trend_report.top_five_failure_codes.title_with_production_line", {
                failureType: defaultFailureTypeTranslatedName,
                productionLine: selectedProductionLine?.name,
            });
        }

        if (selectedFailureType?.id && !selectedProductionLine?.id) {
            return t("locations.trend_report.top_five_failure_codes.title", {
                failureType: defaultFailureTypeTranslatedName,
            });
        }

        return t("locations.trend_report.top_five_failure_codes.title_no_selected");
    }, [
        defaultFailureTypeTranslatedName,
        selectedFailureType?.id,
        selectedProductionLine?.id,
        selectedProductionLine?.name,
        t,
    ]);

    const topFiveSecondaryFailureCodesTitle = useMemo(() => {
        if (selectedProductionLine?.id && selectedFailureCode?.id) {
            return t("locations.trend_report.top_five_secondary_failure_codes.title_with_production_line", {
                failureCode: defaultFailureCodeTranslatedName,
                productionLine: selectedProductionLine?.name,
            });
        }

        if (selectedFailureCode?.id && !selectedProductionLine?.id) {
            return t("locations.trend_report.top_five_secondary_failure_codes.title", {
                failureCode: defaultFailureCodeTranslatedName,
            });
        }

        return t("locations.trend_report.top_five_secondary_failure_codes.title_no_selected");
    }, [
        defaultFailureCodeTranslatedName,
        selectedFailureCode?.id,
        selectedProductionLine?.id,
        selectedProductionLine?.name,
        t,
    ]);

    useEffect(() => {
        if (millCountryCode) {
            setCountryCode(millCountryCode);
            handleSetMillCountryCode(millCountryCode);
        } else if (corporateCountryCode) {
            setCountryCode(corporateCountryCode);
            handleSetMillCountryCode(corporateCountryCode);
        }
    }, [millCountryCode, handleSetMillCountryCode, corporateCountryCode]);

    return (
        <>
            <Box className={classes.gridContainer}>
                <ReportCard
                    headerClassName={classes.cardHeader}
                    headerActionsClassName={classes.cardHeaderActions}
                    title={t("locations.trend_report.downtime_summary_by_production_line.title")}
                    headerActions={<DowntimeSummaryByProductionLineCheckbox />}
                >
                    <DowntimeSummaryByProductionLine />
                </ReportCard>
                <ReportCard
                    headerClassName={classes.cardHeader}
                    headerActionsClassName={classes.cardHeaderActions}
                    title={t("locations.trend_report.downtime_summary_by_failure_type.title")}
                    headerActions={<DowntimeSummaryByFailureTypeCheckbox />}
                >
                    <DowntimeSummaryByFailureType countryCode={countryCode} />
                </ReportCard>
                <ReportCard
                    headerClassName={classes.cardHeader}
                    headerActionsClassName={classes.cardHeaderActions}
                    title={topFiveFailureCodesTitle}
                    headerActions={<TopFiveFailureCodesCheckbox />}
                >
                    <TopFiveFailureCodes />
                </ReportCard>
                <ReportCard
                    headerClassName={classes.cardHeader}
                    headerActionsClassName={classes.cardHeaderActions}
                    title={topFiveSecondaryFailureCodesTitle}
                    headerActions={<TopFiveSecondaryFailureCodesCheckbox />}
                >
                    <TopFiveSecondaryFailureCodes />
                </ReportCard>
                <ReportCard
                    headerClassName={classes.cardHeader}
                    headerActionsClassName={classes.cardHeaderActions}
                    title={
                        selectedFailureType
                            ? t("locations.trend_report.downtime_failure_type_by_machine_center.title", {
                                  failureType: defaultFailureTypeTranslatedName,
                              })
                            : t("locations.trend_report.downtime_failure_type_by_machine_center.title_no_selected")
                    }
                    headerActions={<DowntimeFailureTypeByMachineCenterCheckbox />}
                >
                    <DowntimeFailureTypeByMachineCenter />
                </ReportCard>
                <ReportCard
                    headerClassName={classes.cardHeader}
                    headerActionsClassName={classes.cardHeaderActions}
                    className={classes.weekTrendWrapper}
                    title={`${t("locations.trend_report.13_week_trend.title")} ${
                        weekTrendTitle ? `- ${weekTrendTitle}` : ""
                    }`}
                >
                    <WeekTrend />
                </ReportCard>
            </Box>
        </>
    );
};

export default TrendReport;
